import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Sparkles } from "lucide-react";
import { FadeUp, MaskedLines } from "@/components/Reveal";

const inputCls =
  "w-full bg-transparent border-b border-ink/20 focus:border-coral outline-none py-3 text-base font-light text-ink placeholder:text-ink/35 transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    const onPrefill = (e) => {
      setForm((f) => ({ ...f, ...e.detail }));
      setSent(false);
    };
    window.addEventListener("prefill-enquiry", onPrefill);
    return () => window.removeEventListener("prefill-enquiry", onPrefill);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "conversion", { send_to: "AW-18458700820/gus7CPiQ5oMdEJTY5eFE" });
    }
    window.open("https://www.fantasydancefloors.com/quoteme", "_blank", "noopener,noreferrer");
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, form);
      setSent(true);
      toast.success("Your enquiry is on its way — finish your dream floor in the new tab.");
    } catch (err) {
      toast.error("Something interrupted the magic — please call us at 978-406-5564.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-ivory py-32 sm:py-44 overflow-hidden">
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-blush/15 blur-[120px]" aria-hidden="true" />
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-2 gap-20 items-start relative">
        <div>
          <h2 className="font-serif font-light text-ink leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl mb-10">
            <MaskedLines inView lines={[<>Let's build your</>, <><span className="italic text-coral">dream floor</span>.</>]} />
          </h2>
          <FadeUp delay={0.15}>
            <p data-testid="availability-note" className="flex items-center gap-3 text-[12px] tracking-[0.35em] uppercase text-gold mb-10">
              <Sparkles size={13} strokeWidth={1.5} />
              Now booking 2027 Events
            </p>
            <div>
              <a
                href="tel:9784065564"
                data-testid="contact-phone-link"
                className="flex items-center gap-4 text-ink hover:text-coral transition-colors duration-300 group"
              >
                <span className="w-12 h-12 rounded-full border border-ink/20 flex items-center justify-center group-hover:border-coral transition-colors duration-300">
                  <Phone size={17} strokeWidth={1.5} />
                </span>
                <span className="font-serif text-3xl sm:text-4xl">978-406-5564</span>
              </a>
              <a
                href="mailto:dreams@fantasydancefloors.com"
                data-testid="contact-email-link"
                className="flex items-center gap-4 text-ink hover:text-coral transition-colors duration-300 group mt-6"
              >
                <span className="w-12 h-12 rounded-full border border-ink/20 flex items-center justify-center group-hover:border-coral transition-colors duration-300 shrink-0">
                  <Mail size={17} strokeWidth={1.5} />
                </span>
                <span className="font-serif text-lg sm:text-2xl break-all">dreams@fantasydancefloors.com</span>
              </a>
            </div>
          </FadeUp>
        </div>

        <FadeUp delay={0.25}>
          <div className="bg-white/70 backdrop-blur-xl border border-ink/10 rounded-[2rem] p-8 sm:p-12 shadow-[0_40px_90px_-30px_rgba(45,40,51,0.25)]">
            {sent ? (
              <div data-testid="enquiry-success-message" className="py-16 text-center">
                <p className="font-serif italic text-3xl text-ink mb-4">The butterflies have it.</p>
                <p className="text-ink/60 font-light">Your enquiry has been sent. We'll be in touch very soon — or call us right now at 978-406-5564.</p>
              </div>
            ) : (
              <form data-testid="enquiry-form" onSubmit={submit} className="space-y-7">
                <input data-testid="enquiry-name-input" required placeholder="Your name" value={form.name} onChange={set("name")} className={inputCls} />
                <input data-testid="enquiry-phone-input" required placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} />
                <motion.button
                  type="submit"
                  data-testid="enquiry-submit-button"
                  disabled={sending}
                  className="w-full rounded-full bg-ink text-ivory text-[13px] tracking-[0.2em] uppercase py-4 hover:bg-coral transition-colors duration-300 disabled:opacity-50"
                  whileHover={{ scale: sending ? 1 : 1.02 }}
                  whileTap={{ scale: sending ? 1 : 0.98 }}
                >
                  {sending ? "Sending…" : "Click to Build my Dream Floor"}
                </motion.button>
              </form>
            )}
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
